import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { reaction } from 'mobx';
import { userStore } from '../../auth/store';
import type { HrWorkerPersonal } from './workerDirectoryApi';

interface MemoryValue {
  remember: (person: HrWorkerPersonal) => void;
  find: (personId: string) => HrWorkerPersonal | undefined;
  forget: (personId: string) => void;
}

const WorkerPersonalContext = createContext<MemoryValue | null>(null);

export const WorkerPersonalMemory = ({ children }: { children: ReactNode }) => {
  const [people, setPeople] = useState<Record<string, { epoch: number; host: string; person: HrWorkerPersonal }>>({});

  useEffect(() => reaction(() => userStore.authEpoch, () => setPeople({})), []);

  const remember = (person: HrWorkerPersonal) => {
    const epoch = userStore.authEpoch;
    setPeople((current) => ({ ...current, [person.personId]: { epoch, host: window.location.host, person } }));
  };
  const find = (personId: string) => people[personId]?.epoch === userStore.authEpoch
    && people[personId]?.host === window.location.host ? people[personId]?.person : undefined;
  const forget = useCallback((personId: string) => setPeople((current) => {
    if (!(personId in current)) return current;
    const next = { ...current };
    delete next[personId];
    return next;
  }), []);

  return <WorkerPersonalContext.Provider value={{ remember, find, forget }}>{children}</WorkerPersonalContext.Provider>;
};

export const useWorkerPersonalMemory = (): MemoryValue => {
  const value = useContext(WorkerPersonalContext);
  if (!value) throw new Error('WorkerPersonalMemory is required for Workers pages.');
  return value;
};
